/*
 * Grind picker: whole beans is the only grind that ships, so every other pill
 * records demand instead of selling. Picking one blocks the purchase - add to
 * cart is disabled and the dynamic checkout buttons are hidden - and opens a
 * notify-me form, so the visitor tells us what they wanted instead of buying
 * a grind we cannot make.
 */
if (!customElements.get('grind-picker')) {
  customElements.define(
    'grind-picker',
    class GrindPicker extends HTMLElement {
      connectedCallback() {
        this.availableInputs = Array.from(this.querySelectorAll('input[data-grind-available]'));
        this.soonInputs = Array.from(this.querySelectorAll('input[data-grind-soon]'));
        this.propertyInput = this.querySelector('[data-grind-property]');
        this.request = this.querySelector('[data-grind-request]');
        this.requestHeading = this.querySelector('[data-grind-request-heading]');
        this.tagsInput = this.querySelector('[data-grind-tags]');
        this.headingTemplate = this.requestHeading ? this.requestHeading.textContent.trim() : '';
        this.blockedValue = null;
        this.soonGroup = this.querySelector('[data-grind-soon-group]');
        this.variantSoldOut = this.productForms.some((productForm) =>
          productForm.querySelector('[type="submit"]')?.hasAttribute('disabled'),
        );
        this.syncSoonToStock();

        this.soonInputs.forEach((input) => {
          input.addEventListener('change', () => this.onRequest(input));
        });

        this.availableInputs.forEach((input) => {
          input.addEventListener('change', () => this.onAvailableSelected(input));
        });

        // Every grind on this product is a request: nothing here can be bought.
        if (this.availableInputs.length === 0) {
          this.blockedValue = 'all';
          this.blockPurchase();
        }

        // A variant change re-renders the buy buttons and re-enables the submit
        // button, so the block has to be laid down again afterwards. PUB_SUB_EVENTS
        // and subscribe are script-scoped consts, not window properties.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            // A quick add modal publishes under the page's own section id, so the
            // product id is what tells a change in the modal from one on this page.
            const ownProductId = this.closest('product-info')?.dataset.productId;
            const sourceProductId = data?.html?.querySelector?.('product-info')?.dataset.productId;
            if (ownProductId && sourceProductId && ownProductId !== sourceProductId) return;

            if (data?.variant) {
              this.dataset.variantId = data.variant.id;
              this.dataset.variantPrice = data.variant.price;
            }
            // The re-rendered section is the only honest account of whether the new
            // variant can be bought: the hidden variant id input keeps whatever
            // disabled attribute it was printed with, so it goes stale the moment
            // the shopper switches variant.
            if (data?.html && data?.sectionId) {
              this.variantSoldOut =
                data.html.getElementById(`ProductSubmitButton-${data.sectionId}`)?.hasAttribute('disabled') ?? true;
            }
            this.syncSoonToStock();
            if (this.blockedValue) requestAnimationFrame(() => this.blockPurchase());
          });
        }

        // Belt and braces: anything else that re-enables the button while a grind
        // we cannot ship is selected gets corrected too.
        this.submitObserver = new MutationObserver(() => {
          if (!this.blockedValue) return;
          const enabled = this.productForms.some(
            (productForm) => productForm.querySelector('[type="submit"]')?.hasAttribute('disabled') === false,
          );
          if (enabled) this.blockPurchase();
        });
        this.productForms.forEach((productForm) => {
          this.submitObserver.observe(productForm, { attributes: true, subtree: true, attributeFilter: ['disabled'] });
        });

        // Coming back from a successful (or failed) form post: keep the panel open
        // so the visitor sees the outcome instead of a silently reset page.
        if (this.querySelector('[data-grind-posted]') && this.request) {
          this.request.hidden = false;
        }
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.submitObserver?.disconnect();
      }

      get productForms() {
        const scope = this.closest('product-info') || document;
        return Array.from(scope.querySelectorAll('product-form'));
      }

      onRequest(input) {
        const value = input.value;
        this.blockedValue = value;

        this.soonInputs.forEach((other) => {
          const label = this.querySelector(`label[for="${other.id}"]`);
          if (label) label.classList.toggle('grind-picker__pill--requested', other === input);
        });

        if (this.request) {
          this.request.hidden = false;
          if (this.requestHeading && this.headingTemplate) {
            this.requestHeading.textContent = this.headingTemplate.replace('[grind]', value);
          }
        }

        if (this.tagsInput) {
          this.tagsInput.value = [
            'grind-request',
            `grind-${this.slugify(value)}`,
            `grind-product-${this.dataset.productHandle}`,
          ].join(', ');
        }

        this.blockPurchase();
        this.track(value);
        this.recordCartAttribute(value);
      }

      onAvailableSelected(input) {
        this.blockedValue = null;
        if (this.propertyInput) this.propertyInput.value = input.value;
        this.soonInputs.forEach((other) => {
          this.querySelector(`label[for="${other.id}"]`)?.classList.remove('grind-picker__pill--requested');
        });
        if (this.request) this.request.hidden = true;
        this.unblockPurchase();
      }

      /*
       * A grind request only means something next to a bag that can be bought, so
       * the coming soon pills step aside while the selected weight is sold out and
       * return with a weight in stock. A request that was open when they left falls
       * back to the first grind we ship. A product with nothing but coming soon
       * grinds keeps them, or the picker would be left with no pills at all.
       */
      syncSoonToStock() {
        if (!this.soonGroup || this.availableInputs.length === 0) return;

        this.soonGroup.hidden = this.variantSoldOut;
        if (this.variantSoldOut && this.blockedValue) {
          const fallback = this.availableInputs[0];
          fallback.checked = true;
          this.onAvailableSelected(fallback);
        }
      }

      /*
       * Disables add to cart and pulls the dynamic checkout buttons, which would
       * otherwise skip the cart entirely and sell the grind we just said we cannot
       * ship. The pre-block state is remembered so an already sold-out variant is
       * not quietly re-enabled when the visitor switches back to whole beans.
       */
      blockPurchase() {
        // A sold-out variant is already unbuyable for a reason of its own, and that
        // reason is the more useful one to show: switching the grind back would not
        // let the shopper buy it either.
        const label = this.variantSoldOut
          ? window.variantStrings?.soldOut || 'Sold out'
          : this.dataset.blockedLabel || 'Not available yet';

        this.productForms.forEach((productForm) => {
          productForm.toggleSubmitButton?.(true, label);
          productForm.querySelector('.product-form__buttons')?.classList.add('grind-picker-blocked');
        });

        this.toggleQuantity(false);
      }

      /*
       * The variant's own availability decides the button once the block lifts, so
       * a sold-out variant is never re-enabled by us on the way back to a grind we
       * can ship.
       */
      unblockPurchase() {
        this.productForms.forEach((productForm) => {
          if (this.variantSoldOut) {
            productForm.toggleSubmitButton?.(true, window.variantStrings?.soldOut);
          } else {
            productForm.toggleSubmitButton?.(false);
          }
          productForm.querySelector('.product-form__buttons')?.classList.remove('grind-picker-blocked');
        });

        this.toggleQuantity(true);
      }

      /*
       * How many bags of a grind we cannot sell is not a question worth asking, so
       * the quantity box goes away with the purchase rather than sitting there
       * inert next to a dead button.
       */
      toggleQuantity(show) {
        if (this.dataset.hideQuantity !== 'true') return;

        const scope = this.closest('product-info') || document;
        scope.querySelectorAll('.product-form__quantity').forEach((quantity) => {
          quantity.hidden = !show;
        });
      }

      /*
       * Reports the request to analytics. Shopify's Google channel runs GA4 inside
       * the web pixel sandbox, where window.gtag does not exist, so the event is
       * published to Shopify's customer events and a custom pixel forwards it to
       * GA4 - see docs/grind-demand-tracking.md. gtag and dataLayer are called too,
       * in case GA4 or GTM is ever installed directly in the theme instead.
       *
       * Every click is sent, with first_request marking the first time this visitor
       * asked for this grind on this product, so demand can be read as unique
       * people or as raw intensity without picking one now.
       */
      track(value) {
        const key = `${this.dataset.productHandle}:${this.slugify(value)}`;
        const seen = this.seenRequests();
        const first = !seen.includes(key);

        if (first) {
          seen.push(key);
          this.storeRequests(seen);
        }

        const price = Number(this.dataset.variantPrice);
        const payload = {
          grind: value,
          product: this.dataset.productTitle,
          product_handle: this.dataset.productHandle,
          variant_id: this.dataset.variantId,
          first_request: first,
          value: Number.isFinite(price) ? price / 100 : undefined,
          currency: this.dataset.currency,
        };

        try {
          window.Shopify?.analytics?.publish('grind_request', payload);
        } catch (error) {
          // Analytics must never block the page.
        }
        window.gtag?.('event', 'grind_request', payload);
        window.dataLayer?.push({ event: 'grind_request', ...payload });
        document.dispatchEvent(new CustomEvent('grind:request', { detail: payload }));
      }

      seenRequests() {
        try {
          return JSON.parse(localStorage.getItem('850:grind-requests')) || [];
        } catch (error) {
          return [];
        }
      }

      storeRequests(requests) {
        try {
          localStorage.setItem('850:grind-requests', JSON.stringify(requests.slice(-50)));
        } catch (error) {
          // Private browsing and full storage are not worth failing a click over.
        }
      }

      recordCartAttribute(value) {
        if (this.dataset.recordCartAttribute !== 'true') return;

        const attributes = {};
        attributes['Requested grind'] = `${value} — ${this.dataset.productTitle}`;

        fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes }),
        }).catch(() => {
          // Demand tracking must never block a sale; a failed write is dropped.
        });
      }

      slugify(value) {
        return value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }
    },
  );
}
